import React, { useRef, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useResponsive } from "../../hooks/useResponsive";
import KanbanColumn from "./KanbanColumn";
import Todo from "../../components/ToDo";
import MobileTodo from "../../features/board/components/Kanban/mobileKanban/MobileTodo";

function KanbanContainer({
  workflowSteps,
  projects,
  boardName,
  onDragEnter,
  onDragEnd,
  callback,
  isMyTaskView = false,
}) {
  const boardRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [maxHeight, setMaxHeight] = useState(0);
  const [isTeam, setIsTeam] = useState(searchParams.get("switch") === "true");
  const isMobile = useResponsive("down", "md");

  // Priority ordering for card sorting
  // const priorityOrder = {
  //   Critical: 1,
  //   High: 2,
  //   Medium: 3,
  //   Low: 4,
  // };

  // Update team status when search params change
  useEffect(() => {
    const switchParam = searchParams.get("switch");
    setIsTeam(switchParam === "true");
  }, [searchParams]);

  // Handle drag scrolling
  const handleOnDrag = (e) => {
    const board = boardRef.current;
    if (!board) return;

    const { clientX, clientY } = e;
    const buffer = 50;
    const scrollSpeed = 25;

    // Horizontal scroll
    if (clientX - board.offsetLeft < buffer) {
      board.scrollBy({ left: -scrollSpeed, behavior: "smooth" });
    } else if (board.offsetLeft + board.clientWidth - clientX < buffer) {
      board.scrollBy({ left: scrollSpeed, behavior: "smooth" });
    }

    // Vertical scroll
    if (clientY - board.offsetTop < buffer) {
      board.scrollBy({ top: -scrollSpeed, behavior: "smooth" });
    } else if (board.offsetTop + board.clientHeight - clientY < buffer) {
      board.scrollBy({ top: scrollSpeed, behavior: "smooth" });
    }
  };

  // Group cards by workflow steps
  const groupedCardByWorkFlowSteps = projects.reduce((acc, item) => {
    const stepId = item?.card?.workflow_step_id;
    const genStepId = item?.generic_step_id;

    // Use generic step ID if available, otherwise use workflow step ID
    const targetId = isMyTaskView ?  genStepId || stepId : stepId;

    if (!acc[targetId]) {
      acc[targetId] = [];
    }
    acc[targetId].push(item);
    return acc;
  }, {});

  // Calculate max height for columns
  useEffect(() => {
    const calculateMaxHeight = Object.values(groupedCardByWorkFlowSteps)?.map(
      (cardGroup) => (Array.isArray(cardGroup) ? cardGroup.length : 0)
    );

    const tallestHeight = Math.max(...calculateMaxHeight);
    setMaxHeight(tallestHeight);
  }, [groupedCardByWorkFlowSteps]);

  // Sort projects by priority
  // const sortByPriority = (projects) => {
  //   return projects?.sort(
  //     (a, b) =>
  //       priorityOrder[a?.card?.priority] - priorityOrder[b?.card?.priority]
  //   );
  // };

  // Sort projects by card sequence
  const sortByCardSequence = (projects) => {
    return projects?.sort((a, b) => {
      const seqA = a?.card?.card_sequence ?? 0;
      const seqB = b?.card?.card_sequence ?? 0;
      return seqA - seqB;
    });
  };

  // Get todo projects
  // const todoProjects =
  //   sortByPriority(projects.filter((card) => card.card.is_todo)) || [];
  const todoProjects =
    sortByCardSequence(projects.filter((card) => card.card.is_todo)) || [];


  return (
    <div
      ref={boardRef}
      className={`${isMyTaskView ? "board-card-scroll-btn" : ""} d-flex`}
      style={isMyTaskView ? {
        overflowX: "scroll",
      }: {}}
      onDragOver={handleOnDrag}
    >
      {/* Render Todo section based on device */}
      {isMobile ? (
        <MobileTodo projects={todoProjects} height={221} boardName={boardName} />
      ) : (
        <Todo
          name="Quick Tasks"
          stage="todo-column"
          projects={todoProjects}
          height={270}
          maxHeight={isTeam ? maxHeight : ""}
          key={`quick-tasks-column`}
          id={`quick-tasks-column`}
          boardName={boardName}
        />
      )}

      {/* Render Kanban columns */}
      {workflowSteps.map((column) => {
        // const filteredProjects = sortByPriority(
        //   groupedCardByWorkFlowSteps[column?.workflow_step_id] || []
        // );

        const filteredProjects = sortByCardSequence(
          groupedCardByWorkFlowSteps[column?.workflow_step_id] || []
        );

        return (
          <KanbanColumn
            key={column?.workflow_step_id}
            boardName={boardName}
            id={column?.workflow_step_id}
            name={column?.status}
            stage={column?.workflow_step_id}
            projects={filteredProjects}
            onDragEnter={onDragEnter}
            onDragEnd={onDragEnd}
            callback={callback}
            maxHeight={isTeam ? maxHeight : ""}
            isMyTaskView={isMyTaskView}
          />
        );
      })}
    </div>
  );
}

export default React.memo(KanbanContainer); 